import React, { useState, useEffect, useMemo } from "react";
import Btn from "../../Components/Btn/Btn";
import "./Hero.css";
import { Link as ScrollLink } from "react-scroll";
import { NavLink } from "react-router-dom";
import projects from "../../data/projects";
import emailjs from "emailjs-com";

// Helper to generate Cloudinary srcset
const getCloudinarySrcset = (
  imageUrl,
  baseParams = "f_auto,q_auto:good,dpr_auto"
) => {
  const widths = [200, 300, 400, 600, 800];
  return widths
    .map(
      (width) =>
        `${imageUrl.replace(
          "/upload/",
          `/upload/${baseParams},w_${width}/`
        )} ${width}w`
    )
    .join(", ");
};

const Hero = () => {
  const [loadedImages, setLoadedImages] = useState({});
  const [showCustomModal, setShowCustomModal] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    budget: "",
    description: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);

  const allProjects = projects;
  const projectsPerRow = Math.ceil(allProjects.length / 3);
  const firstRowProjects = allProjects.slice(0, projectsPerRow);
  const secondRowProjects = allProjects.slice(
    projectsPerRow,
    2 * projectsPerRow
  );
  const thirdRowProjects = allProjects.slice(2 * projectsPerRow);

  useEffect(() => {
    if (firstRowProjects.length > 0) {
      const lcpImage = firstRowProjects[0];
      const preloadLink = document.createElement("link");
      preloadLink.rel = "preload";
      preloadLink.as = "image";
      preloadLink.href = lcpImage.image.replace(
        "/upload/",
        "/upload/f_auto,q_auto:good,dpr_auto,w_400/"
      );
      document.head.appendChild(preloadLink);
      return () => document.head.removeChild(preloadLink);
    }
  }, [firstRowProjects]);

  useEffect(() => {
    emailjs.init("45HF9ePqGIfPeMdf9");
  }, []);

  const memoizedFirstRow = useMemo(
    () =>
      firstRowProjects.map((p) => ({
        ...p,
        srcset: getCloudinarySrcset(p.image),
      })),
    [firstRowProjects]
  );
  const memoizedSecondRow = useMemo(
    () =>
      secondRowProjects.map((p) => ({
        ...p,
        srcset: getCloudinarySrcset(p.image),
      })),
    [secondRowProjects]
  );
  const memoizedThirdRow = useMemo(
    () =>
      thirdRowProjects.map((p) => ({
        ...p,
        srcset: getCloudinarySrcset(p.image),
      })),
    [thirdRowProjects]
  );

  const imageSizes =
    "(max-width: 480px) 130px, (max-width: 768px) 160px, (max-width: 1024px) 190px, 220px";

  const budgetOptions = [
    { value: "1500-3000", label: "1.500€ - 3.000€" },
    { value: "3000-7000", label: "3.000€ - 7.000€" },
    { value: "7000+", label: "7.000€+" },
  ];

  const handleCustomProjectClick = (e) => {
    e.preventDefault();
    setShowCustomModal(true);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const templateParams = {
      client_name: formData.name,
      client_phone: formData.phone,
      project_budget:
        budgetOptions.find((opt) => opt.value === formData.budget)?.label ||
        formData.budget,
      project_description: formData.description,
      date: new Date().toLocaleDateString("ro-RO", {
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      }),
      to_email: "yrdagency00@gmail.com",
    };

    try {
      await emailjs.send("service_dfpxm76", "template_tabghbs", templateParams);

      setShowSuccessMessage(true);
      setShowCustomModal(false);

      setTimeout(() => {
        setShowSuccessMessage(false);
        setFormData({ name: "", phone: "", budget: "", description: "" });
      }, 4000);
    } catch (error) {
      console.error("Error sending email:", error);
      alert("A apărut o eroare. Vă rugăm să încercați din nou.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const closeModal = () => {
    setShowCustomModal(false);
    setFormData({ name: "", phone: "", budget: "", description: "" });
  };

  return (
    <section className="hero" id="home">
      <div className="row">
        <div className="text">
          <h1 className="text-1">
            Platforme Web <span className="marker">Performante</span>, Create
            pentru Afacerea Ta.
          </h1>
          <ul>
            <li>
              <span>&#x2714;</span> Soluții personalizate, perfect adaptate
              nevoilor tale
            </li>
            <li>
              <span>&#x2714;</span> Dezvoltare focusată pe calitate și
              scalabilitate
            </li>
            <li>
              <span>&#x2714;</span> Prețuri transparente – <b>de la 1500€</b>
            </li>
          </ul>
          <div className="btns">
            <a href="#" onClick={handleCustomProjectClick}>
              <Btn variant={"accent"}>Cere o Ofertă</Btn>
            </a>
            <NavLink to={"/portofoliu"} className="secondary-cta">
              Vezi portofoliul
            </NavLink>
          </div>
        </div>

        <div className="infinite-carousel">
          {/* First row */}
          <div className="carousel-row carousel-row-1">
            <div className="carousel-track">
              {[
                ...memoizedFirstRow,
                ...memoizedFirstRow,
                ...memoizedFirstRow,
              ].map((project, index) => {
                const isLcpCandidate = index < projectsPerRow && index === 0;
                const cloudinaryUrl = project.image.replace(
                  "/upload/",
                  "/upload/f_auto,q_auto:good,dpr_auto/"
                );
                return (
                  <div
                    key={`row1-${project.id}-${index}`}
                    className="carousel-card"
                    data-category={project.categories[0]}
                  >
                    <img
                      src={cloudinaryUrl}
                      srcSet={project.srcset}
                      sizes={imageSizes}
                      alt={project.title}
                      loading={isLcpCandidate ? "eager" : "lazy"}
                      onLoad={() =>
                        !isLcpCandidate &&
                        setLoadedImages((prev) => ({
                          ...prev,
                          [`row1-${project.id}-${index}`]: true,
                        }))
                      }
                      className={
                        isLcpCandidate
                          ? ""
                          : loadedImages[`row1-${project.id}-${index}`]
                          ? "loaded"
                          : ""
                      }
                    />
                  </div>
                );
              })}
            </div>
          </div>

          {/* Second row - all lazy, with blur-up */}
          <div className="carousel-row carousel-row-2">
            <div className="carousel-track">
              {[
                ...memoizedSecondRow,
                ...memoizedSecondRow,
                ...memoizedSecondRow,
              ].map((project, index) => {
                const cloudinaryUrl = project.image.replace(
                  "/upload/",
                  "/upload/f_auto,q_auto:good,dpr_auto/"
                );
                return (
                  <div
                    key={`row2-${project.id}-${index}`}
                    className="carousel-card"
                    data-category={project.categories[0]}
                  >
                    <img
                      src={cloudinaryUrl}
                      srcSet={project.srcset}
                      sizes={imageSizes}
                      alt={project.title}
                      loading="lazy"
                      onLoad={() =>
                        setLoadedImages((prev) => ({
                          ...prev,
                          [`row2-${project.id}-${index}`]: true,
                        }))
                      }
                      className={
                        loadedImages[`row2-${project.id}-${index}`]
                          ? "loaded"
                          : ""
                      }
                    />
                  </div>
                );
              })}
            </div>
          </div>

          {/* Third row - all lazy, with blur-up */}
          <div className="carousel-row carousel-row-3">
            <div className="carousel-track">
              {[
                ...memoizedThirdRow,
                ...memoizedThirdRow,
                ...memoizedThirdRow,
              ].map((project, index) => {
                const cloudinaryUrl = project.image.replace(
                  "/upload/",
                  "/upload/f_auto,q_auto:good,dpr_auto/"
                );
                return (
                  <div
                    key={`row3-${project.id}-${index}`}
                    className="carousel-card"
                    data-category={project.categories[0]}
                  >
                    <img
                      src={cloudinaryUrl}
                      srcSet={project.srcset}
                      sizes={imageSizes}
                      alt={project.title}
                      loading="lazy"
                      onLoad={() =>
                        setLoadedImages((prev) => ({
                          ...prev,
                          [`row3-${project.id}-${index}`]: true,
                        }))
                      }
                      className={
                        loadedImages[`row3-${project.id}-${index}`]
                          ? "loaded"
                          : ""
                      }
                    />
                  </div>
                );
              })}
            </div>
          </div>

          <div className="carousel-gradient-left"></div>
          <div className="carousel-gradient-right"></div>
          <div className="view-all-link">
            <NavLink to="/portofoliu" className="view-all">
              Vezi toate lucrările noastre →
            </NavLink>
          </div>
        </div>
      </div>

      {/* Custom Project Modal */}
      {showCustomModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={closeModal}>
              &times;
            </button>

            <div className="modal-header">
              <h3>Hai să construim ceva remarcabil.</h3>
              <p>
                Completează formularul și hai să discutăm despre proiectul tău.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="contact-form">
              <div className="form-group">
                <label htmlFor="name">Nume complet *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  placeholder="Introdu numele tău complet"
                />
              </div>

              <div className="form-group">
                <label htmlFor="phone">Număr de telefon *</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  required
                  placeholder="Introdu numărul de telefon"
                />
              </div>

              <div className="form-group">
                <label htmlFor="budget">Buget estimat *</label>
                <select
                  id="budget"
                  name="budget"
                  value={formData.budget}
                  onChange={handleInputChange}
                  required
                >
                  <option value="">Selectează bugetul</option>
                  {budgetOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="description">Descrierea proiectului *</label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  required
                  placeholder="Descrie-ne proiectul tău, ce funcționalități ai nevoie, designul dorit, etc."
                  rows="4"
                />
              </div>

              <div className="form-info">
                <p>
                  Vei fi contactat în maximum 24 de ore cu o ofertă
                  personalizată pentru proiectul tău.
                </p>
              </div>

              <button
                type="submit"
                className="submit-btn"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Se trimite..." : "Trimite cererea"}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Success Message */}
      {showSuccessMessage && (
        <div className="success-overlay">
          <div className="success-message">
            <div className="success-icon">✓</div>
            <h3>Cererea a fost trimisă cu succes!</h3>
            <p>
              Vei fi contactat în maximum 24 de ore cu o ofertă personalizată.
            </p>
            <p>Se închide automat...</p>
          </div>
        </div>
      )}
    </section>
  );
};

export default Hero;
